import {ChangeDetectionStrategy, Component, computed, effect, Injectable, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MøtebehandlingsopptakTranskripsjonModell, MøteId, MøteModell, MøtetranskripsjonRessursClient, OppsummertTranskribertMøtereferatModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatButtonModule} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MøteTranskripsjon, MøteTranskripsjonFeil} from "web-ng2/moduler/møte/tjenester/MøteTranskripsjon";
import {Mikrofonstatus} from "felles-ng2/tjenester/Mikrofonopptak";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {ErrorResponsStatus, SkjemalåsErrorRespons} from "felles-ng2/tjenester/Skjemalåshåndtering";
import {TranslateModule} from "@ngx-translate/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {ZoneddatetimeSomPlainTimePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainTimePipe";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {Temporal} from "temporal-polyfill";
import {MatDialog} from "@angular/material/dialog";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {AvsluttMøteTranskripsjonDialogKomponent} from "web-ng2/moduler/møte/modale/avslutt-møte-transkripsjon-dialog/avslutt-møte-transkripsjon-dialog";
import {MatTooltip} from "@angular/material/tooltip";
import {NgTemplateOutlet} from "@angular/common";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {Fortsettelsesårsak, StartMøteTranskripsjonDialogKomponent} from "web-ng2/moduler/møte/modale/start-møte-transkripsjon-dialog/start-møte-transkripsjon-dialog";
import Duration = Temporal.Duration;
import {FormaterDurationPipe} from "../../../../../felles-ng2/pipes/FormaterDurationPipe";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";

const debug = initDebugger('MøteLydopptakInnspillingKomponent');

const tosifret = (verdi: number) => String(verdi).padStart(2, '0');

/** Ytterpunktene på vektaksen til Material Symbols-fonten, se src/styles/web-ng2/fonter.scss */
const minsteMikrofonvekt = 100;
const størsteMikrofonvekt = 400;

type Tilstand = 'stopped' | 'started' | 'paused';

/**
 * Gui for å styre transkripsjonen av et møte som pågår.
 *
 * Møtet det tas opptak av kommer fra {@link MøteTranskripsjon}, slik at gui-en viser opptaket som
 * faktisk går uansett hvor den er montert.
 *
 * Opptaket eies av {@link MøteTranskripsjon} og startes et annet sted – se
 * StartMøteTranskripsjonDialogKomponent. Denne komponenten tar altså ikke opp lyd selv, den viser
 * tilstanden til opptaket som går og delegerer pause, fortsett og stopp til tjenesten.
 * Dermed lever opptaket videre selv om gui-en fjernes, feks ved navigering.
 */
@Component({
    selector: 'moete-lydopptak-innspilling',
    templateUrl: 'møte-lydopptak-innspilling.html',
    styleUrl: 'møte-lydopptak-innspilling.scss',
    host: {'data-testid': 'moete-lydopptak-innspilling'},
    imports: [
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatProgressSpinner,
        TranslateModule,
        ZoneddatetimeSomPlainTimePipe,
        FormaterPlainTimePipe,
        FormaterZonedDateTimePipe,
        MatTooltip,
        NgTemplateOutlet,
        FormaterDurationPipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteLydopptakInnspillingKomponent {

    oppsummering = signal<OppsummertTranskribertMøtereferatModell | null>(null)

    /** Transkripsjonene som vises nå, null når de er skjult. Se {@link visTranskripsjoner} */
    transkripsjoner = signal<MøtebehandlingsopptakTranskripsjonModell[] | null>(null);
    lasterTranskripsjoner = signal(false);

    sisteFeil = signal<string | undefined>(undefined);

    /**
     * Sann mens opptaket stoppes. Stoppknappen viser at den arbeider og er låst så lenge det står på,
     * slik at bruker ikke rekker å klikke stopp flere ganger.
     */
    readonly stopper = asynkArbeidPågårSignal();

    /** Møtet transkripsjonen gjelder */
    readonly moete: Signal<MøteModell>;
    /** ASCII-navn fordi Angular sin template-parser ikke godtar 'å' i identifikatorer */
    readonly lydnivaa: Signal<number>;
    /** Skriftvekten mikrofonikonet tegnes med, slik at streken svulmer i takt med {@link lydnivaa} */
    readonly mikrofonvekt: Signal<number>;
    /** Hvor lenge opptaket har vart, som mm:ss – eller t:mm:ss når det har vart over en time */
    readonly varighet: Signal<string>;
    readonly tilstand: Signal<Tilstand>;
    readonly viserPuls: Signal<boolean>;
    readonly stoppKnappDeaktivert: Signal<boolean>;
    /**
     * Låsen som hindrer at to brukere tar opp samme møte, når den ikke lot seg oppdatere – null så
     * lenge opptaket holder låsen. ASCII-navn av samme grunn som {@link lydnivaa}.
     */
    readonly skjemalaasfeil: Signal<SkjemalåsErrorRespons | null>;
    /** Sann når en annen bruker har overtatt låsen, usann når brukeren selv gjorde det i et annet vindu */
    readonly laastAvAnnenBruker: Signal<boolean>;
    /**
     * Sann når mikrofonen ikke har fanget opp lyd på en stund, se {@link MøteTranskripsjon.ingenLydRegistrert}.
     * Usann når mikrofonen er frakoblet eller dempet, det varsles det om for seg, se {@link mikrofonstatus}.
     */
    readonly ingenLyd: Signal<boolean>;
    readonly mikrofonstatus: Signal<Mikrofonstatus>;
    /** Se {@link MøteTranskripsjon.opplastingFeiler} */
    readonly opplastingFeiler: Signal<boolean>;
    readonly ventendeLydfiler: Signal<number>;
    /** Feilen som avbrøt opptaket, se {@link MøteTranskripsjon.avbrudd} */
    readonly avbrudd: Signal<MøteTranskripsjonFeil | null>;
    /** Sann dersom bruker har featurevalg 'ki' aktivert, kun disse har tilgang til å vise transkriberte fragmenter */
    brukerHarKiFeature = computed(() => this.brukerkontekst.autentisertBruker().erFeatureAktivert('ki'));

    constructor(private avhengigheter: MøteLydopptakInnspillingKomponentAvhengigheter,
                private transkripsjon: MøteTranskripsjon,
                private navigasjon: Navigasjon,
                private dialog: MatDialog,
                private oversettelser: Oversettelser,
                private brukerkontekst: BrukerkontekstTilbyder) {
        this.moete = this.transkripsjon.møte as Signal<MøteModell>; //transkripsjon.møte er aldri null når denne komponenten opprettes
        this.lydnivaa = toSignal(this.transkripsjon.lydnivå(), {initialValue: 0});
        //lydnivået nullstilles når opptaket stopper, så ikonet faller tilbake til tynn strek av seg selv
        this.mikrofonvekt = computed(() => Math.round(minsteMikrofonvekt + this.lydnivaa() * (størsteMikrofonvekt - minsteMikrofonvekt)));
        const varighet = toSignal(this.transkripsjon.varighet(), {initialValue: new Duration()});
        this.varighet = computed(() => this.somKlokke(varighet()));
        this.tilstand = computed(() => !this.transkripsjon.erIGang() ? 'stopped' : this.transkripsjon.erPauset() ? 'paused' : 'started');
        this.viserPuls = computed(() => this.tilstand() !== 'stopped');
        this.stoppKnappDeaktivert = computed(() => this.tilstand() === 'stopped' || this.stopper());
        //låsen oppdateres periodisk mens opptaket går, og slutter å bli oppdatert i det den blir overtatt
        this.skjemalaasfeil = this.transkripsjon.skjemalåsErrorRespons;
        this.laastAvAnnenBruker = computed(() => this.skjemalaasfeil()?.status === ErrorResponsStatus.låstAvAnnenBruker);
        this.mikrofonstatus = this.transkripsjon.mikrofonstatus;
        this.ingenLyd = computed(() => this.transkripsjon.ingenLydRegistrert() && this.mikrofonstatus() === 'tilkoblet');
        this.opplastingFeiler = this.transkripsjon.opplastingFeiler;
        this.ventendeLydfiler = this.transkripsjon.ventendeLydfiler;
        this.avbrudd = this.transkripsjon.avbrudd;
    }

    gaaTilMoete() {
        this.navigasjon.gåTilMøte(this.moete().id);
    }

    pause(): void {
        this.transkripsjon.pause();
    }

    fortsett(): void {
        this.transkripsjon.fortsett();
    }

    /**
     * Stopper opptaket og oppsummerer transkripsjonen i en dialog, se
     * AvsluttMøteTranskripsjonDialogKomponent.
     *
     * Siste lydfil lastes opp som en del av stoppen, så dialogen vises først når den er levert – da
     * viser den riktig antall lydopptak. Feiler stoppen vises feilen her, og dialogen utelates.
     */
    async stopp(): Promise<void> {
        this.sisteFeil.set(undefined);
        try {
            await this.stopper.utfør(() => this.transkripsjon.stopp());
        } catch (feil: any) {
            debug('stopp av opptak feilet %o', feil);
            this.sisteFeil.set(this.feilmelding(feil));
            return;
        }

        //møtet beholdes av tjenesten etter stopp, så dette slår bare til dersom opptaket aldri kom i gang
        if (erNil(this.moete())) return;

        const respons = await AvsluttMøteTranskripsjonDialogKomponent.vis(this.dialog, this.moete()!);
        if (respons === 'navigerTilRedigerProtokoll') this.navigasjon.gåTilRedigerMøteProtokoll(this.moete()!.id, true);
    }

    /**
     * Stopper opptaket og lar bruker starte det på nytt, gjerne med en annen mikrofon, se
     * StartMøteTranskripsjonDialogKomponent. Ment for når mikrofonen ikke fanger opp lyd, er koblet
     * fra, eller opptaket er avbrutt – da er det allerede stoppet, og stoppes ikke på nytt.
     *
     * I motsetning til {@link stopp} oppsummeres ikke transkripsjonen – møtet er ikke ferdig, det
     * er bare opptaket som startes på nytt. Lydfilene som er lastet opp så langt beholdes, og det nye
     * opptaket kommer i tillegg til dem.
     */
    /** ASCII-navn fordi Angular sin template-parser ikke godtar 'å' i identifikatorer */
    async startPaaNytt(årsak: Fortsettelsesårsak): Promise<void> {
        this.sisteFeil.set(undefined);
        if (this.transkripsjon.erIGang()) {
            try {
                await this.stopper.utfør(() => this.transkripsjon.stopp());
            } catch (feil: any) {
                debug('stopp av opptak ved bytte av mikrofon feilet %o', feil);
                this.sisteFeil.set(this.feilmelding(feil));
                return;
            }
        }

        const moete = this.moete();
        if (erNil(moete)) return;

        await StartMøteTranskripsjonDialogKomponent.vis(this.dialog, moete, {fortsettelse: årsak});
    }

    /** Fjerner forklaringen på hvorfor opptaket ble avbrutt, og dermed også opptakslinja */
    lukkAvbrudd(): void {
        this.transkripsjon.kvitterAvbrudd();
    }

    /**
     * Henter transkripsjonene av alle lydopptakene møtet har og viser dem, eller skjuler dem igjen
     * dersom de allerede vises.
     *
     * Transkriberingen skjer asynkront på server, så et opptak som nettopp er lastet opp kan ennå
     * være uten transkripsjon. Derfor hentes lista på nytt hver gang den vises.
     */
    async visTranskripsjoner(): Promise<void> {
        if (this.transkripsjoner() != null) {
            this.transkripsjoner.set(null);
            return;
        }

        const moete = this.moete();
        if (erNil(moete)) return;

        this.sisteFeil.set(undefined);
        this.lasterTranskripsjoner.set(true);
        try {
            this.transkripsjoner.set(await this.avhengigheter.transkripsjoner(moete.id));
        } catch (feil: any) {
            debug('lasting av transkripsjoner feilet %o', feil);
            this.sisteFeil.set(feil?.message || String(feil));
        } finally {
            this.lasterTranskripsjoner.set(false);
        }
    }

    //private hjelpemetoder

    /** Feil fra opptaket forklares for bruker, øvrige feil vises slik de er */
    private feilmelding(feil: any): string {
        if (feil instanceof MøteTranskripsjonFeil && feil.type === 'opplasting')
            return this.oversettelser.oversett(i18n('Noen lydfiler kunne ikke lastes opp, og blir ikke med i transkripsjonen'));
        return feil?.message || String(feil);
    }

    /** Varigheten som mm:ss, eller t:mm:ss når opptaket har vart over en time */
    private somKlokke(varighet: Duration): string {
        const totaltSekunder = Math.floor(varighet.total('seconds'));
        const timer = Math.floor(totaltSekunder / 3600);
        const minutter = Math.floor(totaltSekunder / 60) % 60;
        const sekunder = totaltSekunder % 60;

        const minutterOgSekunder = `${tosifret(minutter)}:${tosifret(sekunder)}`;
        return timer > 0 ? `${timer}:${minutterOgSekunder}` : minutterOgSekunder;
    }
}

@Injectable()
export class MøteLydopptakInnspillingKomponentAvhengigheter {

    constructor(private møtetranskripsjonRessurs: MøtetranskripsjonRessursClient) {
    }

    /** Transkripsjonene av alle lydopptakene møtet har, i opptaksrekkefølge */
    transkripsjoner(møteId: MøteId): Promise<MøtebehandlingsopptakTranskripsjonModell[]> {
        return this.møtetranskripsjonRessurs.opptakTranskripsjoner(møteId).somPromise();
    }
}
